import { variationType } from "@domain/catalog/catalog";
import type { VariationProps } from "@domain/catalog/variation";
import Base, { parseDomainData } from "./Base";
import Group from "./Group";
import { DomainModelData, baseAttributes } from "./composition";
import { ElementVariationsInterface } from "@domain/data/Table";
import Light from "./Light";
import { ARTICLE_TYPE_IDS } from "@domain/data/Constants";

interface ComplementParams {
	id?: any
	code: string
	attributes: any
}

export default class Complement extends Base {
	type = "Complement";
	static className = "Complement"

	constructor(vibiaComplementData: ComplementParams, parent?: Group, attributes?: baseAttributes, data?: DomainModelData) {
		super(parent, vibiaComplementData.id, attributes, data)

		this.props = {
			...super.getProps(),
			...vibiaComplementData,
		}

		const colorOptions = this.getAttributeVariations('color')
		const hasColorAssigned = Boolean(this.getAttribute("color"))

		if (!hasColorAssigned && colorOptions.length > 0) {
			const optionValue = this.getDefaultAttributeValue('color')
			this.setAttribute('color', optionValue)
		}


		const controlOptions = this.getAttributeVariations('control')
		if (controlOptions.length === 1) {
			const optionValue = this.getDefaultAttributeValue('control')
			this.setAttribute('control', optionValue)
		}

		const temperatureOptions = this.getAttributeVariations('temperature')
		if (temperatureOptions.length === 1) {
			const optionValue = this.getDefaultAttributeValue('temperature')
			this.setAttribute('temperature', optionValue)
		}

		const installationOptions = this.getAttributeVariations('installation')
		if (installationOptions.length === 1) {
			const optionValue = this.getDefaultAttributeValue('installation')
			this.setAttribute('installation', optionValue)
		}

		if (!this.getAttribute('pendantHeight')) {
			const pendantHeight = this.getDefaultAttributeValue('pendantHeight')

			if (pendantHeight) {
				this.setAttribute('pendantHeight', pendantHeight)
			}
		}

		if (!this.getCode()) {
			this.props.code = data.model
		}

		this.reload()
	}

	setAttributesByReference(reference: string) {
		return Light.prototype.setAttributesByReference.call(this, reference)
	}

	getSubFamilyName() {
		return Light.prototype.getSubFamilyName.call(this)
	}

	getSubFamily() {
		return Light.prototype.getSubFamily.call(this)
	}

	getStatus(...params: Parameters<Light['getStatus']>) {
		return Light.prototype.getStatus.call(this, ...params)
	}


	getInfo() {
		const info = super.getInfo()

		info.article.temperature = null
		info.attributes.temperature = null

		return {
			...info,
			type: 'complement'
		}
	}

	isToolbox(): boolean {
		return this.data?.articleType?.level2?.id === ARTICLE_TYPE_IDS.level2.toolboxId
	}

	getArticleSummary(): { finish: VariationProps | null; pendantHeight?: any; control?: VariationProps | null; installation?: VariationProps | null } {
		const finish: VariationProps | null = this.getAttributeVariation("color")
		const pendantHeightValue = this.getAttributeVariation("pendantHeight")
		const pendantHeight = {
			...pendantHeightValue,
			description: `${pendantHeightValue?.standard} m`,
			value: pendantHeightValue?.standard
		}

		const toolboxAttributes: { control?: VariationProps | null; installation?: VariationProps | null } = {}
		if (this.isToolbox()) {
			const control: VariationProps | null = this.getAttributeVariation("control")
			const controlOptions = this.getAttributeVariations("control")
			if (control || controlOptions?.length > 0) toolboxAttributes.control = control ?? null

			const installation: VariationProps | null = this.getAttributeVariation("installation")
			const installationOptions = this.getAttributeVariations("installation")
			if (installation || installationOptions?.length > 0) toolboxAttributes.installation = installation ?? null
		}

		return {
			finish: finish ?? null,
			...(pendantHeightValue?.standard ? { pendantHeight } : {}),
			...toolboxAttributes
		}
	}

	getArticleSummaryVariations() {
		const finishes = this.getAttributeVariations("color");

		const summaryVariations = {
			finishes: finishes ?? null
		}
		return summaryVariations;
	}

	getProductTypology() {
		const type1 = this.data.articleType?.level1?.description?.en
		const type2 = this.data.articleType?.level2?.description?.en
		const type3 = this.data.articleType?.level3?.description?.en
		return type3 ?? type2 ?? type1 ?? null
	}

	getTypeId() {
		const typeId = this.data.articleType?.level2?.id
		return typeId ?? null
	}

	getModel(): string {
		return this.getCode()
	}

	getCode(): string {
		return this.props.code
	}

	getComplementOptions(): ElementVariationsInterface {
		return super.getElementOptions()
	}

	getDefaultAttributeValue(attribute: variationType) {
		const options = this.getAttributeVariations(attribute)
		if (attribute === "pendantHeight") {
			return options?.standard
		}
		const defaultOption = options?.find(element => element.type === "default")

		return Complement.parseAttributeValueFromVariationOption(attribute, defaultOption)
	}

	getApplicationLocations() {
		return this.props.info.applicationLocation
	}

	getLights() {
		return []
	}

	getComplements() {
		return {
			element: this,
			options: {}
		}
	}

	getLightingEffect(): any {
		return {}
	}

	getLightingEffectLevel() {
		return -1
	}

	fromJSON(json, options?: any, instance?: Base, modelData?: DomainModelData) {
		return Complement.fromJSON(json, options, instance, modelData)
	}

	static fromJSON(json, options?: any, instance?: Base, modelData?: DomainModelData) {
		return super.fromJSON(json, options, instance ?? new this(json.props, null, null, json.domainData ?? modelData))
	}

}
